import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../apiBase';

const AuthContext = createContext();
const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || "sso";

// Clés de stockage
const TOKEN_KEY = 'token';
const SSO_TOKEN_KEY = 'sso_token';
const USER_KEY = 'user_data';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Mode SSO : uniquement sessionStorage
    // Mode Local : localStorage (persiste après fermeture)
    if (AUTH_MODE === "sso") {
      return sessionStorage.getItem(SSO_TOKEN_KEY);
    } else {
      return localStorage.getItem(TOKEN_KEY);
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // En mode SSO, nettoyer les anciennes sessions localStorage
    if (AUTH_MODE === "sso") {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Restaurer les données utilisateur
      const storage = AUTH_MODE === "sso" ? sessionStorage : localStorage;
      const storedUser = storage.getItem(USER_KEY);
      
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          console.error('Error parsing stored user data');
          logout();
          return;
        }
      }
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API_URL}/api/auth/verify`);
      setLoading(false);
    } catch (error) {
      logout();
    }
  };

  // Login classique (Mode Local uniquement)
  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        username,
        password
      });
      
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      
      // Stocker dans localStorage pour le mode local
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur de connexion'
      };
    }
  };

  // Login SSO (Mode SSO uniquement)
  const loginWithSSO = async (accessToken, userData) => {
    try {
      setToken(accessToken);
      setUser(userData);
      
      // Stocker dans sessionStorage pour le mode SSO (expire à la fermeture)
      sessionStorage.setItem(SSO_TOKEN_KEY, accessToken);
      sessionStorage.setItem(USER_KEY, JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      setLoading(false);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: 'Erreur de connexion SSO'
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    
    // Nettoyer les deux types de stockage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(SSO_TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    
    delete axios.defaults.headers.common['Authorization'];
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithSSO, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
